// Bella Vita frontend — booking wizard state.
//
// Owner-locked requirements (see PROJECT_RULES.md §10, §12):
//  - refresh-safe: a booking in progress survives a page reload
//  - versioned: schema id = CONFIG.bookingStateVersion; stale/corrupt -> reset
//  - Odoo-account required at confirmation; `customer` holds booking contact details
//  - single salon: NO branch/location field, ever
//  - never persist card/payment-instrument details
//  - cleared/rotated after a successful booking
//
// Persistence area: sessionStorage (per-tab, ephemeral) — matches the rule
// "prefer sessionStorage for unfinished booking state".

import { CONFIG } from '../config.js';
import { createPersistedSlot } from './persist.js';

// V2: no mandatory party-size step — the flow starts at Person 1 and the
// customer adds people/services from the basket. The specialist step is
// policy-driven (skipped when every picked service is `automatic`).
export const STEPS = ['services', 'specialist', 'date', 'time', 'customer', 'review'];

const slot = createPersistedSlot({
  key: 'bv.booking',
  version: CONFIG.bookingStateVersion,
  area: 'session',
  validate: (d) => d && Array.isArray(d.picks) && typeof d.partySize === 'number',
});

function freshState() {
  return {
    step: 0,
    referralCode: null,
    promoCode: '',
    promoQuote: null,
    partySize: 1,
    activePerson: 1,
    // { uid, personId, serviceId, specialistId?:string, noPreference?:boolean }
    picks: [],
    specialistId: 'any',  // 'any' == no preference (whole-booking default)
    dateISO: null,        // 'YYYY-MM-DD' (Date is reconstructed in the UI)
    time: null,           // 'HH:MM'
    customer: { name: '', phone: '', email: '', notes: '' },
    // payment: only the CHOSEN METHOD id is ever kept here — never card data.
    paymentMethod: null,
    agreedTerms: false,
    // Stable across refresh/retry for one unfinished confirmation attempt.
    idempotencyKey: null,
    // set once createBooking() succeeds so /confirmation renders from the
    // server result and stale confirmations are not re-shown.
    confirmation: null,
  };
}

const subs = new Set();
let state = slot.read() || freshState();
const incomingReferral = new URLSearchParams(window.location.search).get('ref');
if (incomingReferral !== null) {
  state.referralCode = incomingReferral.trim().toUpperCase();
  slot.write(state);
  const url = new URL(window.location.href);
  url.searchParams.delete('ref');
  window.history.replaceState(window.history.state, '', url);
}

export const booking = {
  get() {
    return state;
  },

  ensureIdempotencyKey() {
    if (!state.idempotencyKey) {
      if (!globalThis.crypto?.randomUUID) throw new Error('Secure idempotency key generation is unavailable');
      state = { ...state, idempotencyKey: globalThis.crypto.randomUUID() };
      persist();
      emit();
    }
    return state.idempotencyKey;
  },

  /** Shallow-merge a patch, persist, notify. */
  patch(patch) {
    state = { ...state, ...patch };
    persist();
    emit();
    return state;
  },

  /** Mutate via callback (for nested arrays/objects), then persist + notify. */
  update(mutator) {
    mutator(state);
    persist();
    emit();
    return state;
  },

  /** Wipe in-progress state (post-confirmation, or on fail-safe reset). */
  reset() {
    state = freshState();
    slot.clear();
    emit();
    return state;
  },

  /** Store the server's createBooking() result; drop volatile wizard fields. */
  finalize(confirmation) {
    state = { ...freshState(), confirmation };
    // keep confirmation only in memory; do not re-persist a completed booking
    slot.clear();
    emit();
    return state;
  },

  subscribe(fn) {
    subs.add(fn);
    return () => subs.delete(fn);
  },
};

function persist() {
  // Defensive: never let card-like fields leak into storage even if added later.
  const { confirmation, ...rest } = state;
  slot.write(rest);
}
function emit() {
  subs.forEach((fn) => fn(state));
}
